import React from 'react';
import { Button, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const ActionButton = ({
  children,
  variant = 'primary', // 'primary' | 'secondary' | 'success' | 'danger' | 'ghost'
  size = 'medium', // 'small' | 'medium' | 'large'
  startIcon,
  endIcon,
  loading = false,
  disabled = false,
  fullWidth = false,
  onClick,
  type,
  sx = {},
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const variantStyles = {
    primary: {
      backgroundColor: isDark ? '#6366f1' : '#1976d2',
      color: '#fff',
      border: 'none',
      '&:hover': {
        backgroundColor: isDark ? '#4f46e5' : '#1565c0',
        boxShadow: isDark
          ? '0 4px 12px rgba(99, 102, 241, 0.4)'
          : '0 4px 12px rgba(25, 118, 210, 0.3)',
      },
    },
    secondary: {
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f7fafc',
      color: isDark ? '#ffffff' : '#1a202c',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#edf2f7',
        borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e1',
      },
    },
    success: {
      backgroundColor: isDark ? '#2563eb' : '#2563eb',
      color: '#fff',
      border: 'none',
      '&:hover': {
        backgroundColor: isDark ? '#1d4ed8' : '#1d4ed8',
        boxShadow: isDark
          ? '0 4px 12px rgba(37, 99, 235, 0.4)'
          : '0 4px 12px rgba(37, 99, 235, 0.3)',
      },
    },
    danger: {
      backgroundColor: '#fa896b',
      color: '#fff',
      border: 'none',
      '&:hover': {
        backgroundColor: '#f97155',
        boxShadow: '0 4px 12px rgba(250, 137, 107, 0.4)',
      },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: isDark ? '#cbd5e1' : '#4a5568',
      border: 'none',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f7fafc',
        color: isDark ? '#ffffff' : '#1a202c',
      },
    },
    outline: {
      backgroundColor: 'transparent',
      color: isDark ? '#818cf8' : '#1976d2',
      border: isDark ? '1px solid #6366f1' : '1px solid #1976d2',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(99, 102, 241, 0.1)' : 'rgba(25, 118, 210, 0.08)',
      },
    },
  };

  const sizeStyles = {
    small: {
      px: 1.5,
      py: 0.5,
      fontSize: '0.8125rem',
      minHeight: 32,
    },
    medium: {
      px: 2.5,
      py: 1,
      fontSize: '0.875rem',
      minHeight: 40,
    },
    large: {
      px: 3,
      py: 1.25,
      fontSize: '0.9375rem',
      minHeight: 48,
    },
  };

  return (
    <Button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      fullWidth={fullWidth}
      startIcon={!loading && startIcon}
      endIcon={!loading && endIcon}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        ...variantStyles[variant],
        ...sizeStyles[size],
        '&:disabled': {
          backgroundColor: isDark ? '#475569' : '#e2e8f0',
          color: isDark ? '#718096' : '#94a3b8',
          border: 'none',
          boxShadow: 'none',
        },
        ...sx,
      }}
    >
      {loading ? (
        <CircularProgress
          size={size === 'small' ? 16 : size === 'large' ? 24 : 20}
          sx={{ color: 'inherit' }}
        />
      ) : (
        children
      )}
    </Button>
  );
};

export default ActionButton;
